import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, Mail, Globe } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const SUBPROCESSORS = [
  {
    name: 'MongoDB Atlas',
    purpose: 'Primary database for user accounts, sessions, content',
    location: 'EU-West (Ireland) · AWS region eu-west-1',
    dataTypes: 'User accounts, conversations, mood logs, audit events',
    url: 'https://www.mongodb.com/legal/privacy-policy',
  },
  {
    name: 'Cloudflare',
    purpose: 'CDN, DDoS protection, edge caching for static assets',
    location: 'Global edge network',
    dataTypes: 'Request metadata only (no payloads logged)',
    url: 'https://www.cloudflare.com/privacypolicy/',
  },
  {
    name: 'Postmark',
    purpose: 'Transactional email delivery (welcome, password reset, status alerts)',
    location: 'United States',
    dataTypes: 'Email address, user name, dynamic email content',
    url: 'https://postmarkapp.com/privacy-policy',
  },
  {
    name: 'Twilio',
    purpose: 'SMS for two-factor authentication and crisis-safety phone outreach',
    location: 'United States',
    dataTypes: 'Phone numbers, one-time passcodes',
    url: 'https://www.twilio.com/legal/privacy',
  },
  {
    name: 'Anthropic (Claude Sonnet 4.5)',
    purpose: 'AI Practice Partner conversation generation',
    location: 'United States · processed on Anthropic infrastructure',
    dataTypes: 'Conversation context (transient, not retained for training)',
    url: 'https://www.anthropic.com/privacy',
  },
  {
    name: 'Stripe',
    purpose: 'Payment processing for HiBFF subscriptions and Hearts packs (merchant of record checkout)',
    location: 'United States / United Kingdom · global processor',
    dataTypes: 'Card metadata only (PAN held by Stripe, never by HiBFF)',
    url: 'https://stripe.com/privacy',
  },
  {
    name: 'Google Play Billing',
    purpose: 'Payment processing for Android in-app subscriptions',
    location: 'United States · global processor',
    dataTypes: 'Purchase tokens only (payment details held by Google, never by HiBFF)',
    url: 'https://policies.google.com/privacy',
  },
  {
    name: 'Google Cloud (Firebase Cloud Messaging)',
    purpose: 'Push notifications to Android app',
    location: 'United States',
    dataTypes: 'Device tokens, notification payloads',
    url: 'https://policies.google.com/privacy',
  },
  {
    name: 'Google OAuth',
    purpose: 'Sign-in with Google identity provider',
    location: 'United States',
    dataTypes: 'Google profile (name, email, profile photo) on user consent',
    url: 'https://policies.google.com/privacy',
  },
  {
    name: 'Capgo',
    purpose: 'Capacitor live updates (Android OTA delivery)',
    location: 'European Union',
    dataTypes: 'App version metadata, anonymous device identifiers',
    url: 'https://capgo.app/privacy',
  },
  {
    name: 'GitHub',
    purpose: 'Source code hosting, security advisories, CI runners',
    location: 'United States',
    dataTypes: 'Source code only — no production user data',
    url: 'https://docs.github.com/site-policy/privacy-policies/github-privacy-statement',
  },
];

const SubprocessorsPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="subprocessors-page">
    <PageMeta
      slug="subprocessors"
      path="/subprocessors"
      title="Subprocessors"
      description="Full list of vendors that process HiBFF data — MongoDB Atlas, Postmark, Twilio, Anthropic, Stripe, Capgo and more. SOC 2 + GDPR DPA evidence."
    />
    <header className="px-6 sm:px-12 py-6 max-w-5xl mx-auto">
      <Link to="/trust-center" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="subprocessors-back">
        <ArrowLeft size={18} /> <span className="font-medium">Trust Center</span>
      </Link>
    </header>

    <main className="px-6 sm:px-12 max-w-5xl mx-auto pb-20">
      <div className="flex items-center gap-3 mb-2 text-purple-600">
        <Database size={28} />
        <span className="font-semibold text-sm uppercase tracking-wide">Subprocessors</span>
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: '#1A1209' }}>
        Vendors that process HiBFF data
      </h1>
      <p className="text-base text-gray-700 mb-8 max-w-3xl">
        These are the third-party services HiBFF uses to deliver the platform.
        Each has been reviewed for GDPR adequacy and signed our Data Processing
        Agreement. We notify enterprise customers of any change to this list at
        least <strong>30 days</strong> before it takes effect.
      </p>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-10">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold text-gray-700">Vendor</th>
              <th className="px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Purpose</th>
              <th className="px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Location</th>
              <th className="px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Data types</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {SUBPROCESSORS.map((sp, i) => (
              <tr key={sp.name} data-testid={`subprocessor-${i}`}>
                <td className="px-4 py-3 align-top">
                  <a href={sp.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-purple-700 hover:underline">
                    {sp.name}
                  </a>
                  <p className="text-xs text-gray-500 mt-0.5 md:hidden">{sp.purpose}</p>
                  <p className="text-xs text-gray-500 lg:hidden">{sp.location}</p>
                </td>
                <td className="px-4 py-3 align-top text-gray-700 hidden md:table-cell">{sp.purpose}</td>
                <td className="px-4 py-3 align-top text-gray-600 hidden lg:table-cell">{sp.location}</td>
                <td className="px-4 py-3 align-top text-gray-600 hidden md:table-cell">{sp.dataTypes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6 mb-6">
        <h2 className="font-semibold text-amber-900 mb-1 flex items-center gap-2">
          <Globe size={18} /> International data transfers
        </h2>
        <p className="text-sm text-amber-800">
          Where data crosses jurisdictions (e.g. UK → US), we rely on the UK
          International Data Transfer Agreement (IDTA) with our US subprocessors,
          combined with EU Standard Contractual Clauses where applicable.
          DPA available on request.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 text-center">
        <h2 className="font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
          <Mail size={18} /> Need a signed DPA, vendor questionnaire, or change-notification subscription?
        </h2>
        <a
          href="mailto:business@hibff.com"
          className="inline-block mt-1 px-5 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold"
          data-testid="subprocessors-email-procurement"
        >
          business@hibff.com
        </a>
      </div>

      <p className="text-xs text-gray-500 mt-8 text-center">Last reviewed: 25 April 2026.</p>
    </main>
  </div>
);

export default SubprocessorsPage;
